import { Box } from '@mui/material'
import React, { useCallback } from 'react'
import type { Control, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Card from '@/components/Card'
import Radio from '@/components/Radio'
import Select from '@/components/Select'
import Title from '@/components/Title'
import { useAppSelector } from '@/store'
import { useGetMetadataQuery } from '@/store/apiService'
import { updateshadeGuideFormValue } from '@/store/shadeGuide'
import { REGION } from '@/utils/constants'

import {
  COLOR_FORM,
  COLOR_VALUE,
  PREP_DROPDOWN_OPTIONS,
  RECIPE_TYPE,
  TARGET_DROPDOWN_OPTIONS,
  getOptions,
} from '../utils/constants'
import { FORM_FIELDS } from '../utils/formTypes'

interface MeasureColorProps {
  control: Control<any>
  setValue: UseFormSetValue<any>
  clearErrors: any
}

interface Metadata {
  [key: string]: any
}

interface SelectOption {
  text: string
  value: string
}

interface RadioOption {
  label: string
  value: string
}

interface SelectProps {
  options: SelectOption[]
  title: string
  tooltipText?: string
  control: Control<any>
  name: FORM_FIELDS
  placeholder?: string
  outlinedLabel?: boolean
}

interface RadioProps {
  name: FORM_FIELDS
  control: Control<any>
  label: string
  options: RadioOption[]
  tooltip?: string
}

interface RegionSelectsProps {
  options: SelectOption[]
  control: Control<any>
  baseFieldName: string
  isValue: boolean
}

// Reusable Select component
const CustomSelect = ({
  options,
  title,
  tooltipText,
  control,
  name,
  placeholder,
  outlinedLabel,
}: SelectProps) => {
  const dispatch = useDispatch()
  const handleChange = useCallback(
    (name: string, value: any) => {
      dispatch(updateshadeGuideFormValue({ name: name as FORM_FIELDS, value }))
    },
    [dispatch],
  )

  return (
    <Select
      options={options}
      title={title}
      tooltipText={tooltipText}
      control={control}
      name={name}
      onFormValueChange={handleChange}
      placeholder={placeholder}
      outlinedLabel={outlinedLabel}
    />
  )
}

// Reusable Radio component
const CustomRadio = ({
  name,
  control,
  label,
  options,
  tooltip,
}: RadioProps) => {
  const dispatch = useDispatch()
  const handleChange = useCallback(
    (name: string, value: string) => {
      dispatch(updateshadeGuideFormValue({ name: name as FORM_FIELDS, value }))
    },
    [dispatch],
  )

  return (
    <Radio
      name={name}
      control={control}
      label={label}
      options={options}
      tooltip={tooltip}
      onFormValueChange={handleChange}
      formStateKey="shadeGuide"
    />
  )
}

// Reusable Region Selects component
const RegionSelects = ({
  options,
  control,
  baseFieldName,
  isValue,
}: RegionSelectsProps) => {
  const { t } = useTranslation()

  const recipeType = useAppSelector(
    (state) => state.shadeGuide.form?.[FORM_FIELDS.RECIPE_TYPE],
  )

  const regions =
    recipeType === RECIPE_TYPE.SINGLE_ZONE
      ? [REGION.MIDDLE]
      : Object.values(REGION)

  const getFieldName = useCallback(
    (region: string) => {
      const regionLower = t(region).toLowerCase()
      const suffix = isValue ? 'value' : 'chroma'
      return `${baseFieldName}_${regionLower}_${suffix}` as FORM_FIELDS
    },
    [baseFieldName, isValue],
  )

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {regions.map((region) => (
        <CustomSelect
          key={region}
          options={options}
          title={t(region)}
          control={control}
          name={getFieldName(region)}
          placeholder={options[0]?.text}
          outlinedLabel
        />
      ))}
    </Box>
  )
}

const MeasureColor: React.FC<MeasureColorProps> = ({
  control,
  setValue,
  clearErrors,
}) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const preColor = useAppSelector(
    (state) => state.shadeGuide.form?.[FORM_FIELDS.PREPARATION_COLOR],
  )

  const targetColor = useAppSelector(
    (state) => state.shadeGuide.form?.[FORM_FIELDS.TARGET_COLOR],
  )

  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as { data: Metadata | undefined; error: any; isLoading: boolean }

  const options = {
    stumpShade: metadata ? getOptions(metadata, 'available_naturaldie') : [],
    vitaOptions: metadata ? getOptions(metadata, 'available_vita_codes') : [],
    vitaClassicOptions: metadata
      ? getOptions(metadata, 'available_vita_classic_codes')
      : [],
    ndCardOptions: metadata
      ? getOptions(metadata, 'available_ndcard_options')
      : [],
  }

  const getCurrentOptions = useCallback(
    (selectedColor: string) => {
      if (selectedColor === COLOR_VALUE.Vita3DMaster) {
        return options.vitaOptions
      } else if (selectedColor === COLOR_VALUE.VitaClassic) {
        return options.vitaClassicOptions
      } else if (selectedColor === COLOR_VALUE.NaturalDieMaterial) {
        return options.ndCardOptions
      }
      return []
    },
    [options],
  )

  const prepColorOptions = getCurrentOptions(preColor)
  const targetColorOptions = getCurrentOptions(targetColor)

  const resetValues = (name: string) => {
    // Clear related region selects when Target Shade or Preparation Color changes
    const regions = Object.values(REGION)

    regions.forEach((region) => {
      const regionLower = region.toLowerCase()
      const valueFieldName = `${name}_${regionLower}_value`
      const chromaFieldName = `${name}_${regionLower}_chroma`
      // Clear value fields
      dispatch(
        updateshadeGuideFormValue({
          name: valueFieldName as FORM_FIELDS,
          value: '',
        }),
      )
      setValue(valueFieldName as any, '')
      clearErrors(valueFieldName as keyof FormData | (keyof FormData)[])

      // Clear chroma fields
      dispatch(
        updateshadeGuideFormValue({
          name: chromaFieldName as FORM_FIELDS,
          value: '',
        }),
      )
      setValue(chromaFieldName as any, '')
      clearErrors(chromaFieldName as keyof FormData | (keyof FormData)[])
    })
  }

  return (
    <>
      <Card>
        <CustomRadio
          name={FORM_FIELDS.RECIPE_TYPE}
          control={control}
          label={t(COLOR_FORM.recipeType.label)}
          options={COLOR_FORM.recipeType.options.map((opt) => ({
            label: t(opt.label),
            value: opt.value,
          }))}
          tooltip={t(COLOR_FORM.recipeType.tooltip)}
        />
      </Card>
      <Card>
        <CustomRadio
          name={FORM_FIELDS.RESTORATION_METHOD}
          control={control}
          label={t(COLOR_FORM.restorationMethod.label)}
          options={COLOR_FORM.restorationMethod.options.map((opt) => ({
            label: t(opt.label),
            value: opt.value,
          }))}
          tooltip={t(COLOR_FORM.restorationMethod.tooltip)}
        />
      </Card>
      <Card>
        <CustomSelect
          options={options.stumpShade}
          title={t(COLOR_FORM.stumpShade.label)}
          tooltipText={t(COLOR_FORM.stumpShade.tooltip)}
          control={control}
          name={FORM_FIELDS.STUMP_SHADE}
        />
      </Card>
      <Card>
        <Select
          options={PREP_DROPDOWN_OPTIONS}
          title={t(COLOR_FORM.preparationColor.label)}
          tooltipText={t(COLOR_FORM.preparationColor.tooltip)}
          control={control}
          name={FORM_FIELDS.PREPARATION_COLOR}
          onFormValueChange={(name, value) => {
            dispatch(
              updateshadeGuideFormValue({ name: name as FORM_FIELDS, value }),
            )
            resetValues(name)
          }}
        />
        <Box ml={3} display="flex" flexDirection="column" gap={2}>
          <RegionSelects
            options={prepColorOptions}
            control={control}
            baseFieldName={FORM_FIELDS.PREPARATION_COLOR}
            isValue={true}
          />
        </Box>
      </Card>
      <Card>
        <Select
          options={TARGET_DROPDOWN_OPTIONS}
          title={t(COLOR_FORM.targetShade.label)}
          tooltipText={t(COLOR_FORM.targetShade.tooltip)}
          control={control}
          name={FORM_FIELDS.TARGET_COLOR}
          onFormValueChange={(name, value) => {
            dispatch(
              updateshadeGuideFormValue({ name: name as FORM_FIELDS, value }),
            )
            resetValues(name)
          }}
        />
        <Box ml={3} display="flex" flexDirection="column" gap={2}>
          <Title fontSize="16px">{t('shadeGuideAI.value')}</Title>
          <RegionSelects
            options={targetColorOptions}
            control={control}
            baseFieldName={FORM_FIELDS.TARGET_COLOR}
            isValue={true}
          />
        </Box>
        <Box ml={3} display="flex" flexDirection="column" gap={2}>
          <Title fontSize="16px">Chroma</Title>
          <RegionSelects
            options={targetColorOptions}
            control={control}
            baseFieldName={FORM_FIELDS.TARGET_COLOR}
            isValue={false}
          />
        </Box>
      </Card>
    </>
  )
}

export default MeasureColor
